type RouteMatch = { route: { path?: string } };

/** A `matchRoutes` bound to an app's route tree, e.g. `(loc) => matchRoutes(routes, loc)`. */
export type RouteMatcher = (location: { pathname: string }) => RouteMatch[] | null;

/** React Router's matched route pattern for a path (`/classes/:classId`), or the raw path when
 * nothing matches. Kept free of React imports so non-hook telemetry can use it too. */
export function getRoutePattern(pathname: string, matchRoutes: RouteMatcher): string {
  const matches = matchRoutes({ pathname });
  if (!matches?.length) return pathname;
  // A layout route with path "/" is truthy but contributes nothing, and joining it in would
  // produce a "//" prefix.
  return `/${matches
    .map((m) => m.route.path)
    .filter((path): path is string => !!path && path !== "/")
    .join("/")}`;
}

// Shapes that are always an opaque identifier, never a hand-written route segment.
// Deliberately conservative: a segment is only replaced when it cannot be a literal route
// name, so real routes such as /OAuth2Callback survive verbatim. Named `:param` placeholders
// come from the router; this is the fallback for whatever it can't match.
const ID_SEGMENT_PATTERNS = [
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // UUID
  /^\d+$/, // numeric id
  /^[0-9a-f]{12,}$/i, // long hex token, which subsumes the 24-char Mongo ObjectId
  /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{20,}$/i, // long opaque token (invite code, JWT segment, ...)
];

// Applied to every URL part before anything else. /OAuth2Callback carries a single-use
// authorization code in its query string, so no code path may let one reach a tag.
const stripQuery = (part: string): string => part.split("?")[0]!;

const scrubIdSegments = (path: string): string =>
  path
    .split("/")
    .map((segment) => (ID_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment)) ? ":id" : segment))
    .join("/");

/** Low-cardinality route string for the `app_broken_route` tag.
 *
 * The hash is part of the result because home, student and teach all use `createHashRouter`:
 * dropping it collapses every event onto one value, which is the blind spot this tag exists to
 * remove. Pass the app's `matchRoutes` (bound to its route tree, as `_app.tsx` already does for
 * `useRouteSpans`) to get real route patterns like `/schools/story/:storyId`; without it the
 * shape-based scrub above is used. Query strings are stripped unconditionally. */
export function normalizeAppBrokenRoute(url: { pathname: string; hash: string }, matchRoutes?: RouteMatcher): string {
  try {
    // `pathname` should hold neither, but a malformed URL must not leak a query string.
    const pathname = scrubIdSegments(stripQuery(url.pathname.split("#")[0]!));
    const hashPath = stripQuery(url.hash.replace(/^#/, ""));
    if (!hashPath) return pathname;

    const pattern = matchRoutes ? getRoutePattern(hashPath, matchRoutes) : hashPath;
    return `${pathname}#${scrubIdSegments(pattern)}`;
    // Telemetry must never break the error handler reporting through it.
    // eslint-disable-next-line no-catch-all/no-catch-all -- best-effort tagging: any failure here must degrade to a placeholder, never propagate out of the error handler
  } catch {
    return "unknown";
  }
}
